import { useTheme } from '@emotion/react';
import { ActionIcon, Button, Menu, Text, Tooltip } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { IconDotsVertical, IconShare } from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import useTranslation from 'next-translate/useTranslation';
import { useContext, useState } from 'react';
import type { AxiosError } from 'axios';

import { InviteCTA } from '~/domains/contest/domains/invite/components/InviteCTA';
import InviteModal from '~/domains/contest/domains/invite/components/InviteModal';
import { useIsTablet } from '~/domains/common/hooks/mediaQueries';
import { cancelContest } from '~/services/contests/contests.service.api';
import { useInvitesManagerEnabled } from '~/domains/invites-manager/hooks/useInvitesManagerEnabled';

import { UserContext } from '../../../../components/providers/UserProvider';
import { reportEvent } from '../../../analytics/events';

type ContestRowMenuProps = {
  hasEntries: boolean;
  contestId: string;
  contestName: string;
  onSuccess?: () => void;
};

function ContestRowMenu({ hasEntries, contestId, contestName, onSuccess }: ContestRowMenuProps) {
  const theme = useTheme();
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();
  const { user } = useContext(UserContext);
  const [inviteModalContestId, setInviteModalContestId] = useState<string>(null);
  const isTablet = useIsTablet();
  const { isInvitesManagerEnabled, navigateToInvitesManager } = useInvitesManagerEnabled();

  const { mutate: handleCancelContest, isLoading: isCancelling } = useMutation({
    mutationFn: async () => cancelContest(contestId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['commissioner/contests', user.id],
      });
      showNotification({
        title: t('cancelContest.success', { name: contestName }),
        message: '',
        color: 'green',
        autoClose: 5000,
      });
      if (onSuccess) {
        onSuccess();
      }
    },
    onError: (error: AxiosError) => {
      const defaultMessage = t('cancelContest.error.message');
      const message = (error?.response?.data as any)?.message || defaultMessage;
      reportEvent('Cancel Contest Error', {
        reason: message,
      });

      showNotification({
        title: t('cancelContest.error.title'),
        message,
        color: 'red',
        autoClose: 2500,
      });
    },
  });

  return (
    <>
      <Menu
        position="bottom-end"
        zIndex={99}
        styles={{
          dropdown: { position: 'absolute' },
          item: { padding: 0 },
        }}
      >
        <Menu.Target>
          <ActionIcon radius="xl">
            <IconDotsVertical color={theme.colors.gray[6]} />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown w={300}>
          {isTablet && (
            <Menu.Item closeMenuOnClick={false}>
              <InviteCTA
                contestId={contestId}
                onClick={() => {
                  if (isInvitesManagerEnabled) {
                    return navigateToInvitesManager(contestId);
                  }
                  return setInviteModalContestId(contestId);
                }}
                variant="text"
                leftIcon={<IconShare />}
              />
            </Menu.Item>
          )}
          <Menu.Item closeMenuOnClick={false}>
            {hasEntries ? (
              <Tooltip
                label={t('cancelContest.tooltip')}
                withinPortal
                events={{ hover: true, focus: true, touch: true }}
              >
                <Button
                  fullWidth
                  radius="sm"
                  variant="default"
                  styles={{
                    root: {
                      width: '150px',
                      border: 0,
                    },
                  }}
                >
                  <Text color="gray.5">{t('cancelContest.cancel')}</Text>
                </Button>
              </Tooltip>
            ) : (
              <Button
                onClick={() => handleCancelContest()}
                loading={isCancelling}
                fullWidth
                radius="sm"
                variant="default"
                styles={{
                  root: {
                    width: '150px',
                    border: 0,
                  },
                }}
              >
                <Text color="red.7">{t('cancelContest.cancel')}</Text>
              </Button>
            )}
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
      <InviteModal
        opened={!!inviteModalContestId}
        onClose={() => setInviteModalContestId(null)}
        contestId={inviteModalContestId}
        contestName={contestName}
      />
    </>
  );
}

export default ContestRowMenu;
